import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { WarningDiamond } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import useAdminLoad from "@/hooks/useAdminLoad";
import { Empty, LoadState, PageHeader, Panel, Pill } from "@/components/admin/AdminUI";
import ConfirmDialog from "@/components/admin/ConfirmDialog";
import VerificationCard from "@/components/admin/VerificationCard";
import { MediaTile, REVIEW_TYPES, TypePill } from "@/components/admin/ReviewMedia";
import { api, selfieUrl } from "@/lib/api";
import { formatDate } from "@/lib/format";

function Selfie({ r }) {
  return <MediaTile src={selfieUrl(r.selfie_url)} alt={`Verification selfie for ${r.new_phone}`} className="w-full shrink-0 sm:w-48" testId={`review-selfie-${r.id}`} />;
}

function PhoneChangeCard({ r, onDecide }) {
  const manual = r.manual_support_review || r.review_flag === "manual_support_review";
  return (
    <Panel className={`flex flex-col gap-6 p-6 sm:flex-row ${manual ? "cw-ring-warning" : ""}`} data-testid={`review-card-${r.id}`}>
      <Selfie r={r} />
      <div className="flex min-w-0 flex-1 flex-col">
        {manual && (
          <div className="cw-tone-warning mb-4 flex items-start gap-2 rounded-xl px-3 py-2 text-sm" data-testid={`review-manual-flag-${r.id}`}>
            <WarningDiamond size={18} weight="fill" className="mt-0.5 shrink-0" aria-hidden />
            <span><strong className="font-semibold">Manual support review.</strong> Their number changed recently (inside the cooldown). Check carefully.</span>
          </div>
        )}
        <p className="flex flex-wrap items-center gap-2"><TypePill r={r} /><span className="cw-muted text-xs">{formatDate(r.created_at)}</span></p>
        <p className="mt-2 text-lg font-medium tabular-nums">{r.old_phone} <span className="cw-muted">→</span> {r.new_phone}</p>
        <Link to={`/admin/users/${r.user_id}`} className="cw-brand-deep mt-1 w-fit rounded text-sm underline-offset-4 hover:underline" data-testid={`review-user-link-${r.id}`}>View user</Link>
        {r.status !== "pending" ? (
          <div className="mt-auto pt-6 text-sm"><Pill tone={r.status === "approved" ? "success" : "error"} className="capitalize">{r.status}</Pill>
            <span className="cw-muted ml-2">by {r.reviewed_by} · {formatDate(r.reviewed_at)}</span>{r.decision_note && <p className="cw-soft mt-2">“{r.decision_note}”</p>}</div>
        ) : (
          <div className="mt-auto flex gap-2 pt-6">
            <Button onClick={() => onDecide(r, "approve")} data-testid={`review-approve-button-${r.id}`}>Approve</Button>
            <Button variant="outline" onClick={() => onDecide(r, "reject")} data-testid={`review-reject-button-${r.id}`}>Reject</Button>
          </div>
        )}
      </div>
    </Panel>
  );
}

const ReviewCard = (props) => (props.r.type === "onboarding_verification" ? <VerificationCard {...props} /> : <PhoneChangeCard {...props} />);

const DECISION_COPY = {
  onboarding_verification: {
    approve: ["Approve this verification?", "Their profile gets the verified badge.", "Approve verification"],
    reject: ["Reject this verification?", "No badge. They can try again with a new selfie.", "Reject verification"],
  },
};

function decisionCopy(d) {
  if (!d) return ["", "", ""];
  const custom = DECISION_COPY[d.r.type]?.[d.action];
  if (custom) return custom;
  return d.action === "approve"
    ? [`Move this account to ${d.r.new_phone}?`, "The new number becomes the sign-in number right away.", "Approve change"]
    : ["Reject this request?", "The account keeps its current number.", "Reject request"];
}

export default function Reviews() {
  const [status, setStatus] = useState("pending");
  const [type, setType] = useState("all");
  const [includeTest, setIncludeTest] = useState(false);
  const qs = new URLSearchParams({ status });
  if (type !== "all") qs.set("type", type);
  if (includeTest) qs.set("include_test", "true");
  const { data, error, loading, reload } = useAdminLoad(`/admin/reviews?${qs}`);
  const hiddenTest = !includeTest ? data?.hidden_test_count || 0 : 0;
  const [decision, setDecision] = useState(null);
  const [note, setNote] = useState("");
  const decide = async () => {
    await api(`/admin/reviews/${decision.r.id}/${decision.action}`, { body: { reason: note.trim() || null } });
    const onboarding = decision.r.type === "onboarding_verification";
    toast.success(decision.action === "approve" ? (onboarding ? "Verified. The badge is on their profile." : "Approved. The new number is active.") : "Request rejected.");
    setNote("");
    reload();
  };
  const items = data?.items || [];
  return (
    <div data-testid="admin-reviews-page">
      <PageHeader title="Verification" description="Onboarding verifications and phone change requests, each with a selfie. Manual support reviews are listed first." />
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <Tabs value={type} onValueChange={setType}>
          <TabsList aria-label="Request type">{REVIEW_TYPES.map((t) => <TabsTrigger key={t.key} value={t.key} data-testid={`reviews-type-tab-${t.key}`}>{t.label}</TabsTrigger>)}</TabsList>
        </Tabs>
        <Tabs value={status} onValueChange={setStatus}>
          <TabsList aria-label="Status">{["pending", "approved", "rejected"].map((s) => <TabsTrigger key={s} value={s} className="capitalize" data-testid={`reviews-tab-${s}`}>{s}</TabsTrigger>)}</TabsList>
        </Tabs>
      </div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3" data-testid="reviews-test-toggle-row">
        <label htmlFor="reviews-include-test" className="cw-soft inline-flex cursor-pointer items-center gap-3 text-sm">
          <Switch id="reviews-include-test" checked={includeTest} onCheckedChange={setIncludeTest} data-testid="reviews-include-test-toggle" />
          Show test accounts
        </label>
        {hiddenTest > 0 && !loading && (
          <p className="cw-muted text-xs" role="status" data-testid="reviews-hidden-test-note">
            {hiddenTest} test-account item{hiddenTest === 1 ? "" : "s"} hidden
          </p>
        )}
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <div className="space-y-4">
          {items.map((r) => <ReviewCard key={r.id} r={r} onDecide={(rr, action) => setDecision({ r: rr, action })} />)}
          {!items.length && <Panel><Empty title={status === "pending" ? "Nothing to review" : `No ${status} requests`} text="New requests appear here." testId="reviews-empty" /></Panel>}
        </div>
      </LoadState>
      <ConfirmDialog open={!!decision} onOpenChange={(o) => !o && setDecision(null)} testId="review-decision-dialog"
        destructive={decision?.action === "reject"} confirmLabel={decisionCopy(decision)[2]}
        title={decisionCopy(decision)[0]} description={decisionCopy(decision)[1]}
        onConfirm={decide}>
        <div><Label htmlFor="review-note">Note (optional)</Label>
          <Textarea id="review-note" value={note} onChange={(e) => setNote(e.target.value)} className="mt-2" rows={2} data-testid="review-note-input" /></div>
      </ConfirmDialog>
    </div>
  );
}
